<template>
  <div class="filters">
    <div class="f w100p">
      <h2 class="filters__title">
        {{ t("select_items") }}
      </h2>

      <button
        class="btn btn-transparent px12 hd ht light"
        @click="showSearch = !showSearch"
      >
        <i class="icon i-search f16" />
      </button>
    </div>
    <!--    <div class="filters__wrapper"> -->
    <div class="filters__top g g2 gap12">
      <div
        v-if="showSearch || currW >= 768"
        class="form_field"
      >
        <input
          id="adv_search_input"
          v-model="filters.searchText"
          :placeholder="`${t('search')}...`"
          autocomplete="off"
          class="form_input with-icon"
          type="text"
          @input="onFiltersChange"
        >
        <label for="adv_search_input">
          <i class="icon i-search f18 wh43" />
        </label>

        <button
          v-if="filters.searchText"
          class="btn wh43 btn_clear"
          @click="
            filters.searchText = null;
            onFiltersChange();
          "
        >
          <i class="i-times f14" />
        </button>
      </div>
      <div class="filters__top-selects g g2 gap12">
        <UiSelect
          v-model="rarity"
          :disabled="!!rarityProp"
          :items="includedRarities || []"
          :placeholder="t('Rarity')"
          clearable
          translate
          @update:model-value="onRarityChange"
        >
          <template #top-in>
            {{
              (filters as AdvancedSearchInputV2)?.rarities?.length
                ? (filters as AdvancedSearchInputV2)?.rarities?.join(', ')
                : t('any_rarity')
            }}
          </template>
          <template #default-in="{ item }">
            <span class="f">
              <span
                :style="{
                  background: rarityColors[item],
                }"
                class="rarity-dot"
              />
              <span>{{ item }}</span>
            </span>
          </template>
        </UiSelect>

        <UiMultiSelect
          v-model:model="filters.collections"
          :items="collections || []"
          :placeholder="t('collections')"
          class="collections-select"
          clearable
          @on-change="onFiltersChange"
        >
          <template #top-label>
            <div
              class="search-wrapper"
              @click.stop
            >
              <ui-input v-model="collectionsText" />
            </div>
          </template>
          <template #top-in="{ items }">
            {{ items?.length ? items.join(", ") : t("any_collection") }}
          </template>
          <template #default-in="{ item }">
            <img
              v-if="item"
              :alt="`${item} image`"
              :src="getImageFromWaxpeer(item, '50:50')"
              class="wh30 mr8"
              loading="lazy"
            >
            {{ item }}
          </template>
        </UiMultiSelect>
      </div>
    </div>
    <div class="filters__bottom">
      <div class="form_field f fs">
        <input
          id="isActive"
          :checked="stattrackProp || filters.isStattrak"
          :disabled="!!rarityProp"
          class="switcher"
          type="checkbox"
          @change="
            filters.isStattrak = !!$event.target.checked;
            onFiltersChange();
          "
        >
        <label for="isActive">StatTrak™</label>
      </div>
      <button
        :class="{ asc: filters.orderBy === OrderBy.LowestPrice }"
        class="btn btn-sort"
        type="button"
        @click="
          filters.orderBy
            = filters.orderBy === OrderBy.HighestPrice
              ? OrderBy.LowestPrice
              : OrderBy.HighestPrice;
          onFiltersChange();
        "
      >
        <span class="btn-sort__wrapper">
          <span class="btn-sort__line btn-sort__line_1" />
          <span class="btn-sort__line btn-sort__line_2" />
          <span class="btn-sort__line btn-sort__line_3" />
        </span>

        <span class="btn-sort__label">{{ t("sort_by_price") }}</span>
      </button>
      <!--      </div> -->
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { AdvancedSearchInputV2, SearchParams } from '~/graphql/generated/schema-types';
import { useResize } from '~/composables/resize.js';
import { OrderBy } from '~/graphql/generated/schema-types';
import { getImageFromWaxpeer } from '~/utils/get-image-url';

const props = defineProps<{
  params: SearchParams
  rarityProp: string | null
  stattrackProp: boolean
}>();
const emit = defineEmits(['onChange']);

const currW = useResize();

const filters = defineModel<Partial<AdvancedSearchInputV2>>('filters', {
  default: () => ({} as Partial<AdvancedSearchInputV2>),
});
const { t } = useI18n();
const debounce = useDebounce();
const collectionsText = ref<string | null>('');
const rarity = ref<string | null>('');
const showSearch = ref(false);

const includedRarities = [
  'Consumer Grade',
  'Industrial Grade',
  'Mil-Spec Grade',
  'Restricted',
  'Classified',
];

const rarityColors: Record<string, string> = {
  'Consumer Grade': '#b0c3d9',
  'Industrial Grade': '#5e98d9',
  'Mil-Spec Grade': '#617bff',
  'Restricted': '#9861ff',
  'Classified': '#d63eea',
};

const collections = computed(() =>
  (props.params.collections || [])
    .filter(
      i =>
        !(
          i?.toLowerCase().includes('agent')
          || i?.toLowerCase().includes('sticker')
          || i?.toLowerCase().includes('graffiti')
          || i?.toLowerCase().includes('patch')
          || i?.toLowerCase().includes('pins')
        ),
    )
    .filter(i =>
      i?.toLowerCase()?.includes(collectionsText.value?.toLowerCase() || ''),
    ),
);

const onFiltersChange = debounce(() => {
  emit('onChange', filters.value);
}, 600);

function onRarityChange(value: string | null) {
  filters.value.rarities = value ? [value] : [];
  onFiltersChange();
}

watch(
  () => props.rarityProp,
  (val) => {
    onChangeRarity(val);
  },
);

function onChangeRarity(val: string | null) {
  if (
    rarity.value === val
    && filters.value.rarities?.length === 1
    && filters.value.rarities[0] === val
  ) {
    return;
  }
  rarity.value = val;
  filters.value.rarities = val ? [val] : [];
  emit('onChange', filters.value);
}

onMounted(() => {
  if (props.rarityProp)
    onChangeRarity(props.rarityProp);
});
</script>

<style lang="scss" scoped>
.filters {
  //&__wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
  justify-content: center;
  @media screen and (max-width: 768px) {
    gap: 8px;
  }
  //}
  &__title {
    color: var(--surfaces-on-surface);
    font-weight: 600;
    line-height: 28px; /* 140% */
  }

  .form_field {
    width: 100%;
  }

  &__top {
    width: 100%;
    @media screen and (max-width: 768px) {
      grid-template-columns: 1fr;
    }

    .search-wrapper {
      padding: 8px;
      background: var(--accent-on-primary);
    }

    .collections-select {
      max-width: 200px;
    }
  }

  &__bottom {
    width: 100%;
    display: flex;
    gap: 20px;
    align-items: center;
    justify-content: center;

    .form_field label {
      color: var(--surfaces-on-surface);
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }

    .btn-sort {
      display: flex;
      gap: 6px;

      &__wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        min-width: 18px;
        gap: 3px;
      }

      &__line {
        display: inline-block;
        height: 1.5px;
        border-radius: 2px;
        background: var(--accent-on-surface);
        transition: width 0.3s ease;

        &_1 {
          width: 18px;
        }

        &_2 {
          width: 14px;
        }

        &_3 {
          width: 10px;
        }
      }

      &__label {
        color: var(--surfaces-on-surface);
        font-size: 14px;
        font-weight: 600;
        line-height: normal;
        white-space: nowrap;
      }

      &.asc {
        .btn-sort__line {
          &_1 {
            width: 10px;
          }

          &_2 {
            width: 14px;
          }

          &_3 {
            width: 18px;
          }
        }
      }
    }
  }

  .btn_clear {
    position: absolute;
    top: 0;
    right: 0;
  }

  .rarity-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 8px;
  }
}
</style>

<style lang="scss">
.filters__top-selects {
  @media screen and (max-width: 768px) {
    .cselect_top {
      min-height: 36px;
      height: 36px;
      border-radius: 6px;
      background: var(--surfaces-high-container);
    }
  }
}
</style>
